import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Save, X, Loader2 } from "lucide-react";

/**
 * InlineMarkdownEditor — toggles between a markdown preview block and a
 * Textarea editor with Save / Cancel + autosave-on-blur (5s debounce).
 *
 * The parent owns persistence: this component just calls `onSave(value)`.
 * The promise's resolved boolean (or no throw) is treated as success.
 */
export default function InlineMarkdownEditor({
  label,
  value,
  onSave,
  testid,
  rows = 10,
  debounceMs = 5000,
  placeholder = "",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  const commit = async (nextValue) => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(nextValue);
      dirty.current = false;
      setSavedAt(Date.now());
    } finally {
      setSaving(false);
    }
  };

  const scheduleAutosave = (nextValue) => {
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (dirty.current) commit(nextValue);
    }, debounceMs);
  };

  const onBlur = () => {
    // autosave-on-blur: commit immediately, cancel any pending debounce
    clearTimeout(timer.current);
    if (dirty.current) commit(draft);
  };

  const onSaveClick = async () => {
    clearTimeout(timer.current);
    await commit(draft);
    setEditing(false);
  };

  const onCancelClick = () => {
    clearTimeout(timer.current);
    dirty.current = false;
    setDraft(value ?? "");
    setEditing(false);
  };

  if (!editing) {
    return (
      <details className="rounded-md border border-stone-200" data-testid={`${testid}-section`}>
        <summary className="cursor-pointer px-3 py-2 font-medium flex items-center justify-between">
          <span>{label}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2"
            onClick={(e) => {
              e.preventDefault();
              setEditing(true);
            }}
            data-testid={`${testid}-edit-btn`}
          >
            <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
          </Button>
        </summary>
        <pre
          className="px-3 py-2 whitespace-pre-wrap font-sans text-stone-700 text-sm"
          data-testid={`${testid}-preview`}
        >
          {value || <span className="text-stone-400 italic">No content yet.</span>}
        </pre>
      </details>
    );
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50/30 p-3 space-y-2" data-testid={`${testid}-editor`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm font-medium">{label}</div>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="text-xs text-stone-500 inline-flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> saving…
            </span>
          )}
          {!saving && savedAt && (
            <span className="text-xs text-emerald-700" data-testid={`${testid}-saved-pill`}>
              saved
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={onCancelClick}
            disabled={saving}
            data-testid={`${testid}-cancel-btn`}
          >
            <X className="mr-1 h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={onSaveClick}
            disabled={saving}
            className="bg-stone-900 hover:bg-stone-800 text-white"
            data-testid={`${testid}-save-btn`}
          >
            <Save className="mr-1 h-3.5 w-3.5" /> Save
          </Button>
        </div>
      </div>
      <Textarea
        rows={rows}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => {
          setDraft(e.target.value);
          scheduleAutosave(e.target.value);
        }}
        onBlur={onBlur}
        className="font-mono text-xs"
        data-testid={`${testid}-textarea`}
      />
      <div className="text-[11px] text-stone-500">
        Autosaves 5s after you stop typing (or on blur). Press Save to commit immediately.
      </div>
    </div>
  );
}
